import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAPI } from '../../api';
import { useAuth } from '../../auth/AuthProvider';
import { useQueryParamState } from '../../helpers/query';

interface TenantOption {
	id: string | null;
	name: string;
	strategies: string[];
	last?: string | null;
}

interface AuthStrategiesResponse {
	last?: string;
	lastTenantID?: string | null;
	contextTenantID?: string | null;
	user?: string[];
	available: string[];
	tenants?: TenantOption[];
}

type AuthStrategy = 'local' | 'google' | 'saml' | 'ldap';

const strategyLabels: Record<string, string> = {
	'local': 'Use Username/Email/Password',
	'google': 'Login with Google OAuth',
	'saml': 'Login with SAML',
	'ldap': 'Login with LDAP'
};

const Login = () => {
	const api = useAPI();
	const auth = useAuth();

	const [loginError, setLoginError] = useQueryParamState('error');

	const location = useLocation();

	const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

	if (from) localStorage.setItem('afterlogintarget', from);

	const reauth = !!(location.state as { reauth?: boolean } | null)?.reauth;

	const [username, setUsername] = useState<string | null>(reauth ? (auth?.user ?? null) : null);
	const [showAllStrategies, setShowAllStrategies] = useState(false);
	const [authStrategies, setAuthStrategies] = useState<AuthStrategiesResponse | null>(null);
	const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
	const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);

	const [mode, setMode] = useState<'login' | 'signup'>('login');
	const [signupEmail, setSignupEmail] = useState('');
	const [signupSubmitted, setSignupSubmitted] = useState(false);
	const [signupError, setSignupError] = useState<string | null>(null);
	const [allowSignup, setAllowSignup] = useState(false);

	React.useEffect(() => {
		api.get<{ allowSignup: boolean }>('/api/auth/signup')
		.then(json => setAllowSignup(json.allowSignup))
		.catch(() => { /* feature unavailable — keep hidden */ });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const autoSelectTenant = (tenantOption: TenantOption) => {
		setSelectedTenant(tenantOption);
		const last = tenantOption.last ?? null;
		if (last && tenantOption.strategies.includes(last)) {
			setSelectedStrategy(last);
		}
	};

	const loadAuthStrategies = (user: string) => {
		api.post<AuthStrategiesResponse>('/api/auth/strategies', { username: user })
		.then(json => {
			setAuthStrategies(json);

			if (json.tenants && json.tenants.length === 1) {
				// Single tenant — auto-select it
				autoSelectTenant(json.tenants[0]);
			} else if (json.tenants && json.tenants.length > 1 && json.contextTenantID) {
				// Hostname-derived context tenant takes priority — auto-select if the user has access
				const contextTenant = json.tenants.find(t => t.id === json.contextTenantID) ?? null;
				if (contextTenant) autoSelectTenant(contextTenant);
			} else if (json.tenants && json.tenants.length > 1 && json.lastTenantID !== undefined) {
				// Multiple tenants — auto-select the one the user last logged in with, if still available
				const lastTenant = json.tenants.find(t => t.id === json.lastTenantID) ?? null;
				if (lastTenant) autoSelectTenant(lastTenant);
			} else if (!json.tenants || json.tenants.length === 0) {
				// No tenant info (user not found or no grants) — fall back to system strategies
				if (json.last) setSelectedStrategy(json.last);
			}
			// Multiple tenants: wait for user to choose
		});
	};

	React.useEffect(() => {
		if (reauth && username)
			loadAuthStrategies(username);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function setUsernameClick(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		const form = e.target as HTMLFormElement;
		const usernameInput = form.elements.namedItem('username') as HTMLInputElement;
		const user = usernameInput.value;

		setUsername(user);
		setLoginError(null);

		loadAuthStrategies(user);
	}

	function changeUsername(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();

		setUsername(null);
		setAuthStrategies(null);
		setSelectedTenant(null);
		setSelectedStrategy(null);
		setLoginError(null);
	}

	function changeTenant(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();

		setSelectedTenant(null);
		setSelectedStrategy(null);
	}

	function forgotPassword(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();

		api.post('/api/auth/recovery/resetpassword', { email: username })
		.then(() => {
			setLoginError('Password reset email sent.');
		});
	}

	function renderTenantSelector(tenants: TenantOption[]) {
		return (
			<div>
				<h3>Select a tenant to log in to</h3>

				<hr />

				<div>
					{tenants.map(tenant => (
						<div key={tenant.id ?? '__system__'} style={{ marginTop: '1.5em' }}>
							<button onClick={() => autoSelectTenant(tenant)}>
								{tenant.name}
							</button>
						</div>
					))}
				</div>
			</div>
		);
	}

	function renderStrategySelector(strategy: string) {
		return (
			<div key={strategy}>
				<button onClick={() => setSelectedStrategy(strategy)}>{
					strategyLabels[strategy] || strategy
				} ({strategy})</button>
			</div>
		);
	}

	function renderStrategyUI(strategy: string) {
		const tenantID = selectedTenant?.id ?? null;
		const loginBase = tenantID ? `/api/t/${tenantID}/auth` : `/api/auth`;

		let strategyElements: React.ReactNode = (<div></div>);

		switch (strategy as AuthStrategy) {
			case 'local': {
				strategyElements = (
					<div>
						<h4>{strategy}</h4>

						<form method="POST" action={`${loginBase}/login/local`}>
							<input type="hidden" name="username" value={username ?? ''} />

							<div className="spacedFormGroup">
								<label>Password</label>
								<input type="password" name="password" autoFocus={true} />
							</div>

							<div className="spacedFormGroup">
								<button type="submit">Login</button>
							</div>

							<div className="spacedFormGroup">
								<button onClick={forgotPassword}>Forgot Password</button>
							</div>
						</form>
					</div>
				);

				break;
			}
			case 'google': {
				strategyElements = (
					<div>
						<form method="POST" action={`${loginBase}/login/google`}>
							<input type="hidden" name="username" value={username ?? ''} />

							<div className="spacedFormGroup">
								<button type="submit">Login with Google OAuth</button>
							</div>
						</form>
					</div>
				);
				break;
			}
			case 'saml': {
				strategyElements = (
					<div>
						<form method="POST" action={`${loginBase}/login/saml`}>
							<input type="hidden" name="username" value={username ?? ''} />

							<div className="spacedFormGroup">
								<button type="submit">Login with SAML</button>
							</div>
						</form>
					</div>
				);
				break;
			}
			case 'ldap': {
				strategyElements = (
					<div>
						<form method="POST" action={`${loginBase}/login/ldap`}>
							<input type="hidden" name="username" value={username ?? ''} />

							<div className="spacedFormGroup">
								<label>Password</label>
								<input type="password" name="password" autoFocus={true} />
							</div>

							<div className="spacedFormGroup">
								<button type="submit">Login with LDAP</button>
							</div>
						</form>
					</div>
				);
				break;
			}
			default: {
				strategyElements = (<div key={strategy}>Unrecognised auth strategy '{strategy}'</div>);
				break;
			}
		}

		return strategyElements;
	}

	// Strategies to show in the selector — from selected tenant if available, otherwise from system-level response
	const strategiesToShow = selectedTenant ? selectedTenant.strategies : (authStrategies?.available ?? []);

	// Whether the request arrived via a tenant-associated hostname — if so, "Change Tenant" is not applicable
	const tenantLockedByHostname = !!authStrategies?.contextTenantID;

	async function signupSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSignupError(null);

		try {
			await api.post('/api/auth/signup', { email: signupEmail });
			setSignupSubmitted(true);
		} catch (err: any) {
			setSignupError(err?.message ?? 'An error occurred. Please try again.');
		}
	}

	if (mode === 'signup') {
		return (
			<div className="centeredContainer">
				<div>
					<h1>Fastvue Platform Dev UI — Sign Up</h1>

					{!signupSubmitted && (
						<div>
							<form onSubmit={signupSubmit}>
								<div className="spacedFormGroup">
									Enter your email address<br />
									<input
										type="email"
										name="email"
										autoFocus={true}
										value={signupEmail}
										onChange={e => setSignupEmail(e.target.value)}
									/>
								</div>

								<div className="spacedFormGroup">
									<button type="submit">Sign Up</button>
								</div>
							</form>

							{signupError && (
								<div className="spacedFormGroup" style={{color: "red"}}>{signupError}</div>
							)}

							<div className="spacedFormGroup">
								<button onClick={() => setMode('login')}>Back to Login</button>
							</div>
						</div>
					)}

					{signupSubmitted && (
						<div>
							<div className="spacedFormGroup">
								Check your email at <span style={{fontFamily: 'monospace'}}>{signupEmail}</span> for a link to set up your account.
							</div>

							<div className="spacedFormGroup">
								<button onClick={() => { setMode('login'); setSignupSubmitted(false); setSignupEmail(''); }}>Back to Login</button>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="centeredContainer">
			<div>
				<h1>Fastvue Platform Dev UI Login</h1>

				<div className="spacedFormGroup">
					Target URL:
					<span style={{fontFamily: 'monospace' }}>{from}</span>
				</div>

				{
					reauth &&
					(
						<div className="spacedFormGroup" style={{color: "yellow"}}>You need to re-authenticate to perform this action.</div>
					)
				}

				{
					!username &&
					(
						<div>
							<form onSubmit={setUsernameClick}>
								<div className="spacedFormGroup">
									Enter username or email<br />
									<input type="text" name="username" autoFocus={true} />
								</div>

								<div className="spacedFormGroup">
									<button type="submit">Continue &gt;&gt;</button>
								</div>
							</form>

							{allowSignup && (
								<div className="spacedFormGroup">
									<button onClick={() => setMode('signup')}>Sign Up</button>
								</div>
							)}
						</div>
					)
				}

				{
					// Tenant selection step — shown when user has multiple tenants and hasn't picked one yet
					username && authStrategies && !selectedTenant && authStrategies.tenants && authStrategies.tenants.length > 1 &&
					(
						<div>
							<div>
								Username: <span style={{fontFamily: 'monospace'}}>{username}</span> <button onClick={changeUsername}>(Change Username)</button>
							</div>

							{renderTenantSelector(authStrategies.tenants)}
						</div>
					)
				}

				{
					// Strategy selection step — shown once we have a tenant (or no tenant info) and no strategy selected yet
					username && authStrategies && (selectedTenant || !authStrategies.tenants || authStrategies.tenants.length === 0) && !selectedStrategy &&
					(
						<div>
							<div>
								Username: <span style={{fontFamily: 'monospace'}}>{username}</span> <button onClick={changeUsername}>(Change Username)</button>
							</div>

							{
								selectedTenant &&
								(
									<div>
										Tenant: <span style={{fontFamily: 'monospace'}}>{selectedTenant.name}</span>
										{authStrategies.tenants && authStrategies.tenants.length > 1 && !tenantLockedByHostname && (
											<button onClick={changeTenant}> (Change Tenant)</button>
										)}
									</div>
								)
							}

							<h3>Select a login strategy</h3>

							<hr />

							<div>
								<h5>User's known strategies</h5>
							</div>

							<div>
								{
									authStrategies && authStrategies.user && authStrategies.user.map(strategy =>
										strategy && <div key={strategy} style={{ marginTop: '1.5em' }}>{renderStrategySelector(strategy)}</div>
									)
								}

								{
									authStrategies && (!authStrategies.user || authStrategies.user.length === 0) &&
									(
										<div className="spacedFormGroup" style={{color: "yellow"}}>No known login methods configured for user.</div>
									)
								}
							</div>

							<div>
								<hr />
								<div>
									Or, show all available strategies:
								</div>

								{
									!showAllStrategies &&
									(
										<button onClick={() => setShowAllStrategies(true)}>Show All Strategies</button>
									)
								}

								{
									showAllStrategies &&
									(
										<button onClick={() => setShowAllStrategies(false)}>Hide All Strategies</button>
									)
								}
							</div>

							{
								showAllStrategies &&
								(
									<div>

										<hr />

										<div>
											<h5>All available strategies</h5>
										</div>

										<div>
											{
												strategiesToShow.map(strategy =>
													<div key={strategy} style={{ marginTop: '1.5em' }}>{renderStrategySelector(strategy)}</div>
												)
											}

											{
												strategiesToShow.length === 0 &&
												(
													<div className="spacedFormGroup" style={{color: "red"}}>No valid login methods.</div>
												)
											}
										</div>
									</div>
								)
							}
						</div>
					)
				}

				{
					username && authStrategies && selectedStrategy &&
					(
						<div>
							<div>
								Username: <span style={{fontFamily: 'monospace'}}>{username}</span> <button onClick={changeUsername}>(Change Username)</button>
							</div>

							{
								selectedTenant &&
								(
									<div>
										Tenant: <span style={{fontFamily: 'monospace'}}>{selectedTenant.name}</span>
										{authStrategies.tenants && authStrategies.tenants.length > 1 && !tenantLockedByHostname && (
											<button onClick={changeTenant}> (Change Tenant)</button>
										)}
									</div>
								)
							}

							<div>
								Strategy: <span style={{fontFamily: 'monospace'}}>{selectedStrategy}</span> <button onClick={() => setSelectedStrategy(null)}>(Change Strategy)</button>
							</div>

							<div>
								{renderStrategyUI(selectedStrategy)}
							</div>
						</div>
					)
				}

				{ loginError && (<div className="spacedFormGroup" style={{color: "red"}}>{loginError}</div>) }
			</div>
		</div>
	);
};

export default Login;
